import { type ComponentProps, useEffect, useRef, useState, type ReactNode } from 'react';
import { useRouter } from 'next/router';
import { useTasks, type WebTask } from '../../../lib/tasks';
import { useSessionSpaceFilter } from '../../../lib/use-session-space-filter';
import { useWorkspaces } from '../../../lib/workspaces';
import { useResolvedTaskFarewell, withFarewellSnapshots } from '../../../lib/use-task-resolved-celebration';
import { fireConfetti } from '../confetti';
import { useMyAgents } from '../../../lib/my-agents';
import { useChatOverlayOpen } from '../../../lib/use-chat-overlay';
import { useMaestroWorking } from '../../../lib/maestro-working';
import { MaestroWorkingRow } from './MaestroWorkingRow';
import { useTranslation } from '../../../lib/i18n';
import { useAgentName } from '../../../lib/use-agent-name';
import { useLoadBi } from '../../../lib/use-load-bi';
import { agentTasksAreEmpty, bucketTasks, groupTaskOrigins, recentTasks, splitScheduled } from '../../../lib/agent-task-buckets';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { TASKS_PAGE } from '../../../lib/tasks-bi';
import {
  agentsOf,
  clampTaskFilters,
  filterTasks,
  hasActiveTaskFilters,
  spacesOf,
  type TaskFilterState,
} from '../../../lib/task-filters';
import { parseBucketParam, visibleTaskTabs, type TabKey, type TaskTab } from './buckets';
import {
  actionNeededCardKey,
  actionNeededCards,
  flatByUpdated,
  interleaveActionNeeded,
  isActionNeededCard,
  listsOutreachQuestions,
  resolveActiveTab,
  taskTabKeys,
} from './tasks-list-logic';
import { useOpenAsks, useOutreachQuestions } from '../../../lib/use-island-quickview';
import { useOpenConversationInPlace } from '../../../lib/use-open-side-chat';
import { ActionNeededCard } from './ActionNeededCard';
import { ChaseAskGroup } from './ChaseAskGroup';
import { TaskTabs } from './TaskTabs';
import { TasksFilterBar } from './TasksFilterBar';
import { TaskCard } from './TaskCard';
import { TaskOriginGroup } from './TaskOriginGroup';
import { OutreachSiblingsProvider } from './OutreachSiblingsContext';
import { receivedThreadSiblings } from './OutreachThreadModal';
import { Card } from '../ui';
import { CategoryHeader, CategoryList, CategoryPage, CategoryToolbar } from '../category-view/CategoryView';
import styles from './tasks.module.css';
import { useCategoryRedesign } from '../../../lib/use-category-redesign';
import { TasksBody as TasksBodyLegacy } from './TasksBody.legacy';
import { TabConnectHint } from '../category-view/TabConnectHint';

// THE one Symphony tasks renderer — tabs + list, self-contained (fetches its own data). Two filter axes
// scope it, so one component serves every context:
//   - GLOBAL (no filter)        — Your Agents, the `/tasks` page, the island quick-view's "see all".
//   - PER-WORKSPACE (workspaceId) — a workspace's "Agents at work".
//   - PER-AGENT (agentId)        — the agent screen.
// Tabs are `[Action needed] [Agent Tasks] [Done] [Failed*]`, opening on the first NON-EMPTY one
// (`resolveActiveTab`, latched once below). A leading catch-all `All` tab (the flat list sorted by
// last-updated) exists in the code but is HIDDEN — see `taskTabKeys`. Each status tab is its bucket. The Space name rides in every card's meta line.
// `header` is the host's chrome (a PageHeader on the standalone page);
// embedded hosts (Your Agents, the agent screen) pass none. The tab/filter/ordering logic is the pure
// `tasks-list-logic` module.
//
// `filterable` adds the two-axis filter row (`TasksFilterBar`), and is for the STANDALONE page only — an
// embedded host already fixes its scope, and a picker for an axis the host has decided is a control that
// can only take the screen somewhere the host doesn't want it. It also changes what `workspaceId` MEANS:
// with `filterable` it is the SELECTED Space (read off `/tasks?workspace=<slug>` by the page), so the
// list is fetched account-wide and narrowed here, which is what lets a user widen back to All Spaces.
// Without it, `workspaceId` is a host scope applied server-side, exactly as before.
//
// ⚠️ That move narrows `/tasks?workspace=<slug>` in one respect, and it is a deliberate trade. The server
// scope (`workspaceScopeIds`) resolves a workspace PLUS ITS CHILD PROJECTS; the client filter matches the
// task's own `workspaceId` exactly, and child workspaces aren't in the `useWorkspaces` roster — so a
// child-project task now shows only under All Spaces. Inert while Projects are hidden, and **this must be
// revisited when they ship**: the fix is to match against the Space's whole scope set rather than its id,
// not to restore the scoped fetch, because that fetch is what made the Space picker a one-way door (with
// one Space in the list, `offersAChoice` hides the control and the user cannot widen back out).
function TasksBodyRedesign({
  workspaceId,
  agentId,
  header,
  filterable: filterableProp,
  initialAgentId = '',
  onHome,
  action,
}: {
  workspaceId?: string;
  agentId?: string;
  header?: ReactNode;
  filterable?: boolean;
  /** Hosted by the home's Tasks category: account-wide and filterable, with the Space pick held here
   *  rather than in the URL, which belongs to the home. */
  onHome?: boolean;
  /** Where the agent picker STARTS, for a drill-in from one agent's screen (`/tasks?agent=<id>`).
   *  Unlike `agentId` it is a selection, not a fixed axis: the picker is still drawn and the reader can
   *  widen back to every agent. Applied once, as the seed it is — re-applying it on every render would
   *  make the pick impossible to change. */
  initialAgentId?: string;
  /** The create button, at the toolbar's end — the home's category view passes its own. */
  action?: ReactNode;
}) {
  const { t } = useTranslation();
  const router = useRouter();
  const openConversationInPlace = useOpenConversationInPlace();
  const { logPageClickBi } = useGenericBi();
  const filterable = filterableProp || onHome;
  const [homeSpace, setHomeSpace] = useState('');
  // A standalone page holds its pick for the visit only (`useSessionSpaceFilter`); a host's `workspaceId`
  // without `filterable` is a fixed server scope instead.
  const [pageSpace, setPageSpace] = useSessionSpaceFilter(workspaceId, !!filterableProp && !onHome);
  const selectedSpace = onHome ? homeSpace : filterableProp ? pageSpace : workspaceId;
  const { state } = useTasks(filterable ? undefined : workspaceId);
  useLoadBi('agent-tasks-data', { isLoading: state.kind === 'loading', isError: state.kind === 'error' });

  // Space (workspace) name for a card's meta line — shown on every card (Figma 13641-101528).
  // Resolve from the task's own workspace, falling back to the scoped `workspaceId` when the
  // row doesn't carry one (a workspace-scoped fetch already knows which Space it is).
  const workspacesState = useWorkspaces();
  const spaceName = (id?: string): string | undefined => {
    const wid = id || workspaceId;
    if (!wid) return undefined;
    const list = workspacesState.status === 'ready' ? workspacesState.workspaces : [];
    return list.find((w) => w.id === wid)?.name;
  };

  // The Space's SLUG, for the goal route a provenance line links to (`lib/task-origin.ts`). Resolved from
  // the same roster the name comes from — the row already has the workspace id, and only the roster knows
  // its slug. No slug means the origin is named but not linked, never a link that lands nowhere.
  const spaceSlug = (id?: string): string | undefined => {
    const wid = id || workspaceId;
    if (!wid) return undefined;
    const list = workspacesState.status === 'ready' ? workspacesState.workspaces : [];
    return list.find((w) => w.id === wid)?.slug;
  };

  // The agent a card's task is assigned to — resolved from the team roster, shown on the card's
  // meta line (its genome dot + name), same as the native card and the island quick-view.
  const teamState = useMyAgents();
  // The Maestro mid-turn in the user's chat. Transient and never a task: it is not stored, not counted
  // in any tab, and it disappears when the turn ends. If the turn produces a task or an outreach, THOSE
  // appear as rows in their own right — this line is only "something is happening right now".
  const maestro = useMaestroWorking(useChatOverlayOpen());
  const agentName = (id?: string): string | undefined => {
    if (!id) return undefined;
    const agents = teamState.status === 'ready' ? teamState.team.agents : [];
    return agents.find((a) => a.id === id)?.name?.trim() || undefined;
  };

  // A HOST's `workspaceId` is applied server-side (the fetch is scoped — see `filterable` above, where it
  // is a selection instead); `agentId` narrows client-side. The `all` list is agent-filtered inside
  // `flatByUpdated`; the status buckets are agent-filtered here.
  const { burst: celebrating, held } = useResolvedTaskFarewell(state.kind === 'ready' ? state.tasks : undefined);
  // Applied at the SOURCE, before scoping, filtering, bucketing or the empty-state gate — every one of
  // which then treats a task saying goodbye exactly as it treated it a moment ago. That is what keeps the
  // row in its own bucket, at its own index, inside its own origin group, instead of teleporting to the
  // end of a list or emptying a bucket that then renders "No tasks yet" under the confetti.
  const withFarewell = withFarewellSnapshots(state.kind === 'ready' ? state.tasks : [], held);
  const scoped = withFarewell.filter((task) => !agentId || task.agentId === agentId);

  // The two filter axes. Options are DERIVED FROM THE TASKS IN SCOPE (never the full roster), so a menu can
  // never offer a value that matches nothing — and from `scoped` rather than the narrowed list, so picking
  // an agent doesn't shrink the Space menu out from under the next pick. An axis the host has fixed offers
  // nothing at all, which is how the bar knows not to draw a picker for it.
  const workspaceRoster = workspacesState.status === 'ready' ? workspacesState.workspaces : [];
  const agentRoster = teamState.status === 'ready' ? teamState.team.agents : [];
  // Derived from the WINDOWED tasks, matching both phones. The list itself is windowed to 30 days, so
  // deriving from the raw set would offer a Space whose only task fell out of the window — the picker would
  // list it, selecting it would empty the screen, and "a menu must not contain a value that matches nothing"
  // is the one rule this module's own header sets out.
  const inWindow = recentTasks(scoped);
  const spaceOptions = filterable ? spacesOf(inWindow, workspaceRoster) : [];
  const agentOptions = filterable && !agentId ? agentsOf(inWindow, agentRoster) : [];

  // The Space lives in the URL — the view stays shareable and the back button works — while the agent axis
  // is this component's own. Both are clamped against what the tasks in scope actually support, so a stale
  // selection (a shared link to a Space whose tasks are gone) can't strand the list on an empty screen.
  //
  // The clamp waits for the rosters and the list: until they land there are no options, and clamping
  // against nothing would silently widen a shared `?workspace=` link to every Space for a frame.
  const [agentPick, setAgentPick] = useState(initialAgentId);
  const requested: TaskFilterState = { workspaceId: filterable ? selectedSpace ?? '' : '', agentId: agentPick };
  const resolved = state.kind === 'ready' && workspacesState.status === 'ready' && teamState.status === 'ready';
  const filters = resolved ? clampTaskFilters(requested, spaceOptions, agentOptions) : requested;
  const visible = filterTasks(scoped, filters);

  // `button_name` keeps the row's own snake_case vocabulary (`switch_tab`, `filter_space`); the 198 event's
  // component name is the kebab-case form the analytics keys use.
  const filterBi = (name: string) =>
    logPageClickBi({ ...TASKS_PAGE, button_name: name }, { componentName: name.replace(/_/g, '-') });

  const applyFilters = (next: TaskFilterState) => {
    setAgentPick(next.agentId);
    if (next.workspaceId === filters.workspaceId) return;
    if (onHome) {
      setHomeSpace(next.workspaceId);
      return;
    }
    setPageSpace(next.workspaceId);
  };

  const byTab = bucketTasks(visible);
  const flat = flatByUpdated(visible, agentId);
  // The OUTREACH-ONLY open questions (no task behind them) the island counts and the ledger pushes for — listed
  // in the Action needed tab and counted in its badge, so the tab holds everything the pill says is waiting.
  // Deduped against the waiting tasks' own questions by the island's rule, and only on the unscoped view (see
  // `listsOutreachQuestions`). Read off the same `/api/tasks` fetch as the rows, so the two cannot be a poll apart.
  const outreachRead = useOutreachQuestions();
  const asksRead = useOpenAsks();
  const lists = listsOutreachQuestions({
    hostWorkspaceId: filterable ? undefined : workspaceId,
    hostAgentId: agentId,
    filters,
  });
  // THE shared count (`lib/action-needed-count.ts`, the island pill's own function): question cards over the
  // waiting tasks, plus — on the unscoped view — the outreach questions and open asks the tab lists as cards.
  const { cards: waitingCards, chaseAsks, count: waitingBadge, extraCount: waitingQuestionCount } = actionNeededCards(
    byTab.waiting,
    lists ? outreachRead : null,
    lists ? asksRead : null,
  );
  const counts: Record<TabKey, number> = {
    all: flat.length,
    waiting: waitingBadge,
    inProgress: byTab.inProgress.length,
    done: byTab.done.length,
    failed: byTab.failed.length,
  };

  // The first non-empty tab is the default; a stat card can still deep-link a status tab with `?tab=`.
  // A user pick wins.
  const [picked, setPicked] = useState<TabKey | null>(null);
  // …until a NEW `?tab=` arrives, which drops the pick so the requested tab wins. Without this, landing
  // here from the island's quick-view while this page is already open would keep whatever tab was clicked
  // earlier and silently ignore the request. Native does the same thing with an explicit store signal
  // (`requestAgentTasksTab`) for exactly this case.
  // On the home the URL is the home's: its `?tab=` belongs to the map view, not to this list.
  const requestedTab = onHome ? undefined : router.query.tab;
  useEffect(() => setPicked(null), [requestedTab]);
  const statusTabs = visibleTaskTabs(visible);
  // The strip comes from `taskTabKeys` — the same function the tests pin — rather than being rebuilt
  // inline here. Built inline, re-adding `'all'` would restore the hidden tab with every test still green,
  // which is exactly what the guard is supposed to catch.
  const tabs: TabKey[] = taskTabKeys(visible);
  const active = resolveActiveTab(picked, parseBucketParam(requestedTab), tabs, statusTabs, counts);
  const shown: WebTask[] = active === 'all' ? flat : byTab[active as TaskTab];
  // One lookup for both the row's own resolve state AND the folded-group rows inside it — a group's
  // header already renders from the snapshot `withFarewellSnapshots` substituted above, but its child
  // `TaskCard`s only animate if this reaches them too (see `TaskOriginGroup`'s `resolveOf`).
  const heldResolve = new Map(held.map((entry) => [entry.task.id, { phase: entry.phase, kind: entry.kind }]));
  const resolveOf = (id?: string) => (id ? heldResolve.get(id) : undefined);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const confettiRef = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const canvas = confettiRef.current;
    if (celebrating && canvas && bodyRef.current) {
      fireConfetti(canvas, bodyRef.current);
    }
  }, [celebrating]);
  // Scheduled rows — PENDING with a future `notBefore` — stay IN the In-progress bucket and in its count,
  // but they sit under their own quiet "Later" header at the end of that list: nothing about them moves
  // before their date, so they must not push live work down the screen. Only this tab groups them; the
  // `all` tab already orders them between the live and the finished work (`compareForTaskList`), and a
  // header there would be followed by settled rows it does not describe.
  const { live, later } = active === 'inProgress' ? splitScheduled(shown) : { live: shown, later: [] as WebTask[] };
  // Folded LAST, over the final rendered list — the rule is "consecutive tasks of one origin", so it
  // only means anything once filtering, tab selection and ordering have settled what the reader sees.
  const rows = groupTaskOrigins(live);
  const laterRows = groupTaskOrigins(later);
  // The thread dialog's Previous / Next steps through the outreach rows in the order this list draws them:
  // the live rows, then the "Later" ones (each row's tasks in render order, folds included).
  const outreachRowsInOrder = receivedThreadSiblings([...rows, ...laterRows].flatMap((row) => row.tasks));

  // The tab bar filters a list — with nothing to filter (still loading, errored, or a genuinely empty
  // account) it is just chrome over an empty state, so it only renders once there are tasks to show.
  // An open question with no task behind it is something to show too: an account whose only waiting thing is
  // one must get the strip and the tab, not "No tasks yet".
  // A "While you were away" item counts only at high stakes, but it is still something to show.
  const nothingToShow = agentTasksAreEmpty(scoped) && waitingQuestionCount === 0 && chaseAsks.length === 0;
  const chaseShown = active === 'waiting' ? chaseAsks : [];
  const hasTasks = state.kind === 'ready' && !nothingToShow;
  const questionsShown = active === 'waiting' ? waitingCards : [];
  // The Action needed tab's cards go IN AMONG its rows, by recency (the island's rule); every other tab is its rows.
  const listItems = interleaveActionNeeded(rows, questionsShown);
  const renderItem = (item: (typeof listItems)[number]) =>
    isActionNeededCard(item) ? <ActionNeededCard key={actionNeededCardKey(item)} item={item} /> : renderRow(item);

  // LATCH the resolved tab the first time the strip actually renders. `active`'s fallback is derived from
  // live counts, so without this it keeps re-deriving: resolve the last Action-needed task and the screen
  // silently switches you to Agent Tasks under your thumb; a refresh that brings in a waiting task yanks
  // you off the list you were reading. The old `'all'` default could not change, so this could not happen
  // — it arrived with the fallback and has to be closed with it. Gated on `hasTasks` so an empty-then-
  // populated account latches on the data, not on the empty first paint. Mirrored on iOS/Android.
  useEffect(() => {
    if (hasTasks && picked === null) setPicked(active);
  }, [hasTasks, picked, active]);

  const renderRow = (row: { pulseIdentityId?: string; goalId?: string; tasks: WebTask[]; needsYouCount: number }) => {
    const task = row.tasks[0];
    const origin = row.pulseIdentityId ? `routine-${row.pulseIdentityId}` : row.goalId ? `goal-${row.goalId}` : '';
    return origin ? (
      <TaskOriginGroup
        key={`${origin}-${task.id}`}
        tasks={row.tasks}
        needsYouCount={row.needsYouCount}
        spaceName={spaceName}
        spaceSlug={spaceSlug}
        agentName={agentName}
        goalId={row.goalId}
        resolveOf={resolveOf}
      />
    ) : (
      <TaskCard
        key={task.id}
        task={task}
        spaceName={spaceName(task.workspaceId)}
        spaceSlug={spaceSlug(task.workspaceId)}
        agentName={agentName(task.agentId)}
        resolvePhase={resolveOf(task.id)?.phase}
        resolveKind={resolveOf(task.id)?.kind}
      />
    );
  };

  return (
    <div ref={bodyRef} className={styles.body}>
      {/* The resolve celebration, owned by the LIST because a completing row leaves its bucket and
          unmounts (see useResolvedTaskCelebration). It marks the event, so it plays wherever the reader
          is standing rather than only on the tab the finished row moved to. */}
      {celebrating && <canvas ref={confettiRef} aria-hidden className={styles.resolveConfetti} />}
      {header}
      <CategoryPage>
        {/* The home names the category itself; a standalone page's PageHeader already does. */}
        <CategoryHeader
          title={onHome ? t('categoryView.tasks.title') : undefined}
          description={onHome ? <TabConnectHint tab="tasks" fallback={t('categoryView.tasks.description')} /> : undefined}
          filters={
            hasTasks && filterable ? (
              <TasksFilterBar spaces={spaceOptions} agents={agentOptions} filters={filters} onChange={applyFilters} onBi={filterBi} />
            ) : undefined
          }
        />
        {/* An empty list keeps the toolbar for its create button: a first task has to start somewhere. */}
        {(hasTasks || action) && (
          <CategoryToolbar action={action}>
            {hasTasks && <TaskTabs tabs={tabs} counts={counts} active={active} onSelect={setPicked} />}
          </CategoryToolbar>
        )}

        {state.kind === 'loading' && <LoadingSkeleton />}
        {state.kind === 'error' && <div className={styles.error}>{t('agentTasks.error.title')}</div>}
        {state.kind === 'ready' &&
          (nothingToShow ? (
            <EmptyState />
          ) : shown.length === 0 && questionsShown.length === 0 && chaseShown.length === 0 ? (
            <Card className={styles.empty}>
              {/* An account with 40 tasks that has been narrowed to none has not run out of tasks — saying
                  "No tasks yet" there is simply untrue, and it hides the fact that a filter is what emptied
                  the screen. Mirrors the Files list, which draws the same distinction. */}
              <div className={styles.emptyHint}>{t(hasActiveTaskFilters(filters) ? 'tasks.filter.noMatch' : 'tasks.empty.title')}</div>
            </Card>
          ) : (
            <OutreachSiblingsProvider value={outreachRowsInOrder}>
              <MaestroWorkingRow maestro={maestro} onOpen={() => (maestro.conversationId ? openConversationInPlace(maestro.conversationId) : void router.push('/'))} />
              {listItems.length > 0 && (
                <CategoryList>{questionsShown.length > 0 ? listItems.map(renderItem) : rows.map(renderRow)}</CategoryList>
              )}
              {laterRows.length > 0 && (
                <>
                  <div className={styles.laterHeader}>{t('tasks.section.later', { count: later.length })}</div>
                  <CategoryList>{laterRows.map(renderRow)}</CategoryList>
                </>
              )}
              {/* The chaser's OWED items, after every other row (lib/chase-asks.ts). */}
              <ChaseAskGroup asks={chaseShown} />
            </OutreachSiblingsProvider>
          ))}
      </CategoryPage>
    </div>
  );
}

function EmptyState() {
  const { t } = useTranslation();
  const agent = useAgentName();
  return (
    <Card className={styles.empty}>
      <div className={styles.emptyTitle}>{t('tasks.empty.title')}</div>
      <div className={styles.emptyHint}>{t('tasks.empty.hint.symphony', { maestroAgentName: agent.short })}</div>
    </Card>
  );
}

function LoadingSkeleton() {
  return (
    <div className={styles.list}>
      {[70, 55, 62].map((w, i) => (
        <Card key={i} size="sm" className={styles.skelCard}>
          <div className="orion-shimmer" style={{ height: 14, width: `${w}%` }} />
          <div className="orion-shimmer" style={{ height: 10, width: `${w - 20}%`, marginTop: 10 }} />
        </Card>
      ))}
    </div>
  );
}

/** The redesign rides the activity-map home's release (`lib/use-category-redesign.ts`); off, this is the legacy design. */
export function TasksBody(props: ComponentProps<typeof TasksBodyRedesign>) {
  const redesign = useCategoryRedesign();
  if (!redesign.resolved) return null;
  return redesign.on ? <TasksBodyRedesign {...props} /> : <TasksBodyLegacy {...props} />;
}
